import type {
	CatalogPromotionFixedAmount,
	CatalogPromotionPercent,
	CatalogPromotionProductPromotionPrice,
} from "@pkg/shared/graphql";

// Keeps autocomplete/documentation for the known CMD `__typename` values while still
// accepting the plain (untyped) `string` that the Elevate-mapped `PromotionData.discount.type` is.
type LiteralUnion<T extends string> = T | (string & {});

type CmdPromotionTypename =
	| CatalogPromotionFixedAmount["__typename"]
	| CatalogPromotionPercent["__typename"]
	| CatalogPromotionProductPromotionPrice["__typename"];

type DiscountCalculationInput = {
	// Passed as a plain string rather than a nested `promotion.discount.type` because
	// callers get this value from two structurally different promotion shapes: the PDP
	// query's raw `__typename` union vs. the Elevate-mapped `PromotionData.discount.type`.
	// Accepting just the discriminator string lets both callers pass their own value in
	// without one of them being forced into the other's shape.
	promotionType: LiteralUnion<CmdPromotionTypename>;
	price?: number;
	originalPrice?: number;
	clubPrice?: number;
	canShowClubPrice: boolean;
};

export function usePromotionCalculations(params: DiscountCalculationInput) {
	// Compute the *real* discount amount, computed from the product's original price (ie its omnibus price) vs its current price.
	// This may differ from whatever values the promotion sets, since those are applied against the product base sales price rather than its omnibus price.
	// Also see the elevate connector.
	const totalDiscountAmount = computed(() => {
		const {clubPrice, promotionType: type, canShowClubPrice, price} = params;

		if (price === undefined) {
			return undefined;
		}

		// Only logged-in customers can see the total discount with the possible club discount included
		const discountedPrice = canShowClubPrice ? (clubPrice ?? price) : price;

		const originalPrice =
			params.originalPrice === undefined
				? discountedPrice
				: params.originalPrice;

		if (type === "CatalogPromotionFixedAmount") {
			const amount = originalPrice - discountedPrice;
			return amount > 0 ? amount : undefined;
		}

		if (
			type === "CatalogPromotionPercent" ||
			type === "CatalogPromotionProductPromotionPrice"
		) {
			const percent = (1 - discountedPrice / originalPrice) * 100;
			return percent > 0 ? Math.round(percent) : undefined;
		}

		return undefined;
	});

	return {
		totalDiscountAmount,
	};
}
